// @flow
import React, { PureComponent } from 'react';
import { Link } from 'react-router-dom';

import NewsListElement from 'components/newsletter/NewsListElement';
import BackgroundImage from 'components/fragments/BackgroundImage';
import Breadcrumb from 'components/fragments/Breadcrumb';
import SideBar from 'components/sidebar/Sidebar';

import { ARTICLE_PER_PAGE } from 'constants/articleConstants';
import Pagination from 'components/newsletter/Pagination';
import Loading from 'components/fragments/Loading';
import { withHelmet } from 'infrastructure/seo/Helmet';
import type { State as SidebarType } from 'reducers/sidebarReducer';
import type { ArticleType } from 'types/Article';
import type { FullUserType } from 'types/User';
import { nullLoadingArticle } from 'types/Article';
import { formatFullDate, getDateFromString } from 'utils/dateUtils';
import { STATUS_LOADING, STATUS_FAILURE } from 'constants/statusConstants';

export type StateProps = {
  sidebar: SidebarType,
  tagArticles: ArticleType[],
  tagArticleNumber: number,
  tag: {
    id: number,
    name: string,
    slug: string
  },
  status: string,
  userPref: FullUserType
};

type HistoryProps = {
  history: any
};

type OwnProps = {
  match: {
    params: {
      slug: string
    }
  }
};

export type DispatchProps = {
  fetchTagArticle: (slug: string, page: number, per_page: number) => void,
  fetchGenericSidebar: () => void
};

type Props = StateProps & OwnProps & HistoryProps & DispatchProps;

type State = {
  currentPage: number,
  isMobile: boolean
};

class TagArticle extends PureComponent<Props, State> {
  props: Props;
  state: State = {
    currentPage: 1,
    isMobile: false
  };

  componentDidMount() {
    const { slug } = this.props.match.params;
    const { currentPage } = this.state;
    this.props.fetchTagArticle(slug, currentPage, ARTICLE_PER_PAGE);
    this.props.fetchGenericSidebar();
  }

  componentDidUpdate(prevProps: Props) {
    const { slug } = this.props.match.params;
    const { currentPage } = this.state;
    if (prevProps.match.params.slug !== slug) {
      this.props.fetchTagArticle(slug, currentPage, ARTICLE_PER_PAGE);
    }
    if (this.props.status === STATUS_FAILURE) {
      this.props.history.push('/404');
    }
  }

  onPageChanged = (currentPage: number) => {
    const { slug } = this.props.match.params;
    this.props.fetchTagArticle(slug, currentPage, ARTICLE_PER_PAGE);
    this.setState({ currentPage });
  };

  renderTagArticles = (tagArticles: ArticleType[]): any => {
    const { userPref } = this.props;

    return tagArticles
      .filter((a, i) => i !== 0)
      .map((tagArticle, index) => {
        return (
          <NewsListElement
            key={index}
            url={`/actualites/${tagArticle.label.slug}/${tagArticle.slug}`}
            img={tagArticle.illustration}
            srcset={tagArticle.srcset}
            title={tagArticle.title}
            date={tagArticle.date}
            description={tagArticle.caption ? tagArticle.caption : tagArticle.meta_description}
            userPref={userPref}
            tags={tagArticle.tags}
            excerpt={tagArticle.excerpt !== '' ? tagArticle.excerpt : tagArticle.meta_description}
            readingTime={tagArticle.readingTime}
          />
        );
      });
  };

  renderSidebar = () => {
    const { sidebar } = this.props;
    return <SideBar sidebarContent={sidebar.data} status={sidebar.status} />;
  };

  render() {
    const { tagArticles, tag, status } = this.props;
    const mainArticle: ArticleType = tagArticles.length > 0 ? tagArticles[0] : nullLoadingArticle;

    if (status === STATUS_LOADING) {
      return <Loading isBlue backgroundColor={'#013974'} />;
    }

    return (
      <>
        <section className="banner banner--themes">
          <div className="banner__thumbnail img-blue-dark" style={{ backgroundImage: 'url(/img/banner/xv.jpg)' }}></div>
          <div className="container">
            <Breadcrumb elements={[{ name: 'Accueil', to: '/' }, 'Mots-clés', tag ? tag.name : '']} isDark={true} />
            <h1 className="ft-h2">Dernières actualités {tag ? '#' + tag.name : ''}</h1>
          </div>
        </section>
        <div className="container">
          <div className="row">
            <div className="col col-md-8 col-lg-9 bg-overflow">
              <section>
                <article className="auteur-detail row">
                  <div className="col col-lg-8on9">
                    <div className="row">
                      <div className="col col-lg-8on9 banner-over player-over--themes">
                        <Link
                          to={`/actualites/${mainArticle.label.slug}/${mainArticle.slug}`}
                          className="news news--big mb-4 mb-md-2 mb-lg-5"
                        >
                          <article>
                            <BackgroundImage
                              className="news__thumbnail news--big "
                              illustration={mainArticle.illustration}
                              srcset={mainArticle.srcset}
                              forceState={'medium_large'}
                            />
                            <div>
                              <h3 className="news__title">{mainArticle.title}</h3>
                              <p className="ft-h6">
                                <span className="news__date">
                                  {formatFullDate(getDateFromString(mainArticle.date))}
                                </span>
                              </p>
                            </div>
                            <p className="news--list">
                              <span className="news__desc">{mainArticle.meta_description}</span>
                            </p>
                          </article>
                        </Link>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col col-lg-8on9">
                        {this.renderTagArticles(tagArticles)}
                        <Pagination
                          current={this.state.currentPage}
                          minimum={1}
                          maximum={Math.ceil(this.props.tagArticleNumber / ARTICLE_PER_PAGE)}
                          isMaximumLastPage={
                            this.state.currentPage >= Math.ceil(this.props.tagArticleNumber / ARTICLE_PER_PAGE)
                          }
                          onPageChanged={this.onPageChanged}
                        />
                      </div>
                    </div>
                  </div>
                </article>
              </section>
            </div>
            {this.renderSidebar()}
          </div>
        </div>
      </>
    );
  }
}

export default withHelmet(TagArticle)((Helmet, pageProps) => {
  const articleNumber = pageProps.tagArticles.length;

  if (articleNumber < 3) {
    return (
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>
    );
  }
});
